import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  ChevronDown,
  Download,
  FileJson,
  FileText,
  Link,
  MoreVertical,
  Printer,
  RotateCcw,
  Settings,
  Upload,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import type { SimulatorState } from '@/domain/types';
import { defaultSimulatorState } from '@/domain/defaults';
import { computeProjection } from '@/domain/engine';
import { exportToFile, parseImportFile } from '@/lib/exportImport';
import { exportToMarkdownFile } from '@/lib/exportMarkdown';
import { extractSimulatorState } from '@/hooks/useServerPersistence';
import { usePrintMode } from '@/store/usePrintMode';
import { useSimulatorState } from '@/store/useSimulatorState';

import { ShareDialog } from '@/components/ShareDialog';
import { ShareWithTeamsDialog } from '@/components/ShareWithTeamsDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function isDefaultState(state: SimulatorState): boolean {
  return JSON.stringify(state) === JSON.stringify(defaultSimulatorState);
}

export function Header({ onOpenConfig }: { onOpenConfig: () => void }) {
  const store = useSimulatorState();
  const { importState, resetState } = store;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [teamsDialogOpen, setTeamsDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [pendingState, setPendingState] = useState<SimulatorState | null>(null);

  const handleExport = () => {
    exportToFile(extractSimulatorState(store));
  };

  const handleExportMarkdown = () => {
    const state = extractSimulatorState(store);
    exportToMarkdownFile(state, computeProjection(state));
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const parsed = await parseImportFile(file);
      const current = extractSimulatorState(store);
      if (isDefaultState(current)) {
        importState(parsed);
        toast.success('Scénario importé avec succès.');
      } else {
        setPendingState(parsed);
        setImportOpen(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Impossible d'importer ce fichier.");
    }
  };

  const handleImportConfirm = () => {
    if (pendingState) {
      importState(pendingState);
      setPendingState(null);
      setImportOpen(false);
      toast.success('Scénario importé avec succès.');
    }
  };

  const handlePrint = () => {
    const { setPrinting } = usePrintMode.getState();
    // flushSync force le re-rendu des graphiques en dimensions fixes AVANT le
    // snapshot synchrone de window.print() ; sinon React ne flushe pas à temps
    // et les graphiques restent vides dans le PDF.
    flushSync(() => setPrinting(true));
    window.print();
    setPrinting(false);
  };

  const handleReset = () => {
    resetState();
    setResetOpen(false);
    toast.success('Scénario réinitialisé.');
  };

  return (
    <div className="flex items-center justify-end gap-2 print:hidden">
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileChange}
        aria-hidden
      />

      {/* Desktop : actions secondaires en ligne, Configurer toujours visible */}
      <div className="hidden items-center gap-2 md:flex">
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer />
          Imprimer
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShareDialogOpen(true)}>
          <Link />
          Partager
        </Button>
        <Button variant="outline" size="sm" onClick={() => setTeamsDialogOpen(true)}>
          <Users />
          Équipe
        </Button>
        <Button variant="outline" size="sm" onClick={handleImportClick}>
          <Upload />
          Importer
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download />
              Exporter
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={handleExport}>
              <FileJson />
              JSON (réimportable)
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={handleExportMarkdown}>
              <FileText />
              Markdown (analyse LLM)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" onClick={() => setResetOpen(true)}>
          <RotateCcw />
          Réinitialiser
        </Button>
      </div>

      {/* Mobile : actions secondaires regroupées dans un menu overflow */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="md:hidden">
          <Button variant="outline" size="icon-sm" aria-label="Plus d'actions">
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={handlePrint}>
            <Printer />
            Imprimer / PDF
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setShareDialogOpen(true)}>
            <Link />
            Partager
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setTeamsDialogOpen(true)}>
            <Users />
            Équipe
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleImportClick}>
            <Upload />
            Importer
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Exporter</DropdownMenuLabel>
          <DropdownMenuItem onSelect={handleExport}>
            <FileJson />
            JSON (réimportable)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleExportMarkdown}>
            <FileText />
            Markdown (analyse LLM)
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setResetOpen(true)}>
            <RotateCcw />
            Réinitialiser
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button size="sm" onClick={onOpenConfig}>
        <Settings />
        Configurer
      </Button>

      <ShareWithTeamsDialog open={teamsDialogOpen} onOpenChange={setTeamsDialogOpen} />
      <ShareDialog open={shareDialogOpen} onOpenChange={setShareDialogOpen} />

      {/* Dialog confirmation import */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remplacer le scénario actuel ?</DialogTitle>
            <DialogDescription>
              Votre configuration actuelle sera écrasée par le scénario importé. Cette action est
              irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleImportConfirm}>Importer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmation reset */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le simulateur ?</DialogTitle>
            <DialogDescription>
              Toutes vos données seront effacées et remplacées par le scénario de démonstration par
              défaut. Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleReset}>
              Réinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
